/**
 * O cliente HTTP do Portal — cada resposta VALIDADA pelo contrato (lote P2 · PORTAL-05).
 *
 * ## O defeito que isto fecha
 *
 * A versão anterior fazia `resposta.json() as Promise<T>`: um `as`, que não é uma
 * verificação, é uma promessa do programador ao compilador. Se a API devolvesse três dos
 * seis campos do resumo do parceiro, a tela renderizava `undefined` — e `br(undefined)`
 * dava "R$ 0,00". Um KPI de dinheiro mostrando zero por causa de um campo que não veio é
 * a pior falha possível numa tela financeira: ela não parece falha.
 *
 * Agora o schema do contrato roda em cima do corpo. Corpo fora do contrato vira falha de
 * tipo `contrato`, e a tela mostra erro em vez de mostrar zero.
 *
 * ## Por que o schema entra por parâmetro
 *
 * Porque assim o contrato é do CHAMADOR, e o compilador cobra: não dá para pedir
 * `parceiro/resumo` e tratar o resultado como campanha. O tipo de retorno sai do schema,
 * não de um genérico que quem chama escolhe.
 */
import type { z } from 'zod'
import { FalhaDoPortal, type TipoDeFalha } from './erros.js'

const BASE = '/portal/v1'

/*
  O recorte que a TELA MONTADA assumiu (D-131) — e que toda leitura diz ao servidor.

  O contexto é da sessão, um cookie que as abas dividem, e o servidor o recalcula a cada
  requisição; a tela, não: ela montou um portal e uma empresa (ou Todas) e segue rotulada por
  eles. Sem dizer qual recorte assumiu, a aba em A cuja vizinha escolheu Todas pediria o período
  seguinte e receberia A + B sob o rótulo de A. Com o cabeçalho, o servidor recusa a divergência
  com 409 sem ler nada, e a casca relê a sessão.

  É estado de MÓDULO, como o ouvinte da queda de sessão: quem o define é a casca, no instante em
  que a sessão confirma o recorte e antes de a tela remontada pedir qualquer coisa — e quem o lê
  é cada pedido, sem que o recorte atravesse todos os componentes do caminho.
*/
const CABECALHO_DO_CONTEXTO = 'x-dv-contexto'
let contextoDaTela: string | null = null
let avisarMudancaDeContexto: ((assumido: string) => void) | null = null

/** O recorte no formato do cabeçalho: `<portalId>:<empresaId|todas>`. É também a chave da remontagem. */
export function chaveDoContexto(portalId: string, empresaId: string | null): string {
  return `${portalId}:${empresaId ?? 'todas'}`
}

/** A casca diz o recorte que a tela vai mostrar; `null` fora do portal (login, escolha). */
export function assumirContexto(chave: string | null): void {
  contextoDaTela = chave
}

export function contextoAssumido(): string | null {
  return contextoDaTela
}

/** Quem relê a sessão quando uma leitura volta com o 409 do contexto que mudou. */
export function aoMudarContexto(ouvinte: ((assumido: string) => void) | null): void {
  avisarMudancaDeContexto = ouvinte
}

function tipoDoStatus(status: number): TipoDeFalha {
  if (status === 401) return 'sessao'
  if (status === 403) return 'proibido'
  if (status === 404) return 'ausente'
  if (status === 429) return 'excesso'
  // 4xx que o servidor explicou. Cair em `servidor` fazia "link vencido" e "banco
  // fora do ar" chegarem indistinguíveis à tela.
  if (status >= 400 && status < 500) return 'requisicao'
  return 'servidor'
}

/**
 * @param contexto o recorte que a leitura diz ter assumido, ou `null` para não dizer nenhum
 *                 (os atos: login, contexto, sair). É capturado NA SAÍDA do pedido: a resposta
 *                 que voltar recusada é do recorte que ela levou, e não do que valer depois.
 */
async function bruto(caminho: string, init?: RequestInit, contexto: string | null = null): Promise<unknown> {
  const headers = new Headers({ 'content-type': 'application/json' })
  if (contexto !== null) headers.set(CABECALHO_DO_CONTEXTO, contexto)
  let resposta: Response
  try {
    resposta = await fetch(`${BASE}/${caminho}`, {
      ...init,
      credentials: 'include',
      headers,
    })
  } catch {
    // Sem resposta: rede, DNS, aba fechando. Não é 500 — e a diferença importa para a
    // tela, que aqui oferece "tentar de novo" com honestidade.
    throw new FalhaDoPortal('rede', 'Sem conexão com o servidor.')
  }

  if (!resposta.ok) {
    const corpo: unknown = await resposta.json().catch(() => null)
    const erro = typeof corpo === 'object' && corpo !== null && 'error' in corpo
      ? (corpo as { error?: { code?: string; message?: string } }).error
      : undefined
    const mensagem = erro?.message ?? ''
    /*
      O recorte da tela já não é o da sessão. Quem decide o que fazer é a casca, e ela é avisada
      DAQUI, uma vez por resposta: vale para os blocos e para o clique de download, que não passa
      pela máquina de recursos.
    */
    if (resposta.status === 409 && erro?.code === 'CONTEXTO_MUDOU' && contexto !== null) {
      avisarMudancaDeContexto?.(contexto)
      throw new FalhaDoPortal('contexto', mensagem === '' ? 'Não foi possível concluir.' : mensagem, contexto)
    }
    throw new FalhaDoPortal(
      tipoDoStatus(resposta.status),
      mensagem === '' ? 'Não foi possível concluir.' : mensagem,
    )
  }

  return resposta.json()
}

/**
 * GET validado. O tipo de retorno é o do SCHEMA — não um genérico de quem chama. Leva o recorte
 * que a tela assumiu, quando há um: o servidor recusa a leitura que não bater com a sessão.
 */
export async function pedir<T>(caminho: string, schema: z.ZodType<T>): Promise<T> {
  return conferir(schema, await bruto(caminho, undefined, contextoDaTela), caminho)
}

/** POST validado, para os três atos do Portal (login, contexto, sair). */
export async function enviar<T>(caminho: string, corpo: unknown, schema: z.ZodType<T>): Promise<T> {
  return conferir(schema, await bruto(caminho, { method: 'POST', body: JSON.stringify(corpo) }), caminho)
}

function conferir<T>(schema: z.ZodType<T>, corpo: unknown, caminho: string): T {
  const conferido = schema.safeParse(corpo)
  if (conferido.success) return conferido.data

  /*
    O detalhe vai para o console e a mensagem da tela fica genérica.

    Quem está do outro lado é um cliente ou um parceiro da agência: "esperava string em
    `notas[0].vencimento`" não o ajuda e ainda descreve a forma interna do dado. Quem
    precisa do detalhe é quem mantém o sistema, e para essa pessoa o console é o lugar.
  */
  console.error(`[portal] resposta fora do contrato em ${caminho}`, conferido.error.issues)
  throw new FalhaDoPortal('contrato', 'Não foi possível carregar agora.', caminho)
}
